import { cn } from '@neo/lib/utils'
import { Tooltip } from '@neo/primitives/tooltip'
import { useReactFlow } from '@xyflow/react'
import { Pencil } from 'lucide-react'
import { useMemo } from 'react'
import { VOICEMAIL_DETECTED_INTENT } from '../../constants'
import { useFlowStore } from '../../store'
import type { EdgeData } from '../../types'
import { FlowNodeTypes, type GenericNode, type NodeDataBase } from '../../types'
import { getOutputStateDescription, getOutputStateName } from '../../utils'
import { useOutputStates } from '../../utils/output-states'
import { pendingEdgeSection } from '../../utils/pending-edge'
import { EdgePopover } from './edge-popover'

interface EdgeLabelProps {
  id: string
  labelX: number
  labelY: number
  source: string
  sourceHandleId: string | null
  target: string
  data?: EdgeData
  shouldFade?: boolean
  selected?: boolean
}

export const EdgeLabel = ({
  id,
  labelX,
  labelY,
  source,
  sourceHandleId,
  target,
  data,
  shouldFade,
  selected,
}: EdgeLabelProps) => {
  const { getNode } = useReactFlow()

  // Get fresh node data instead of relying on potentially stale sourceNode prop
  const freshSourceNode = getNode(source) as GenericNode | undefined

  const opStateName = useMemo(
    () =>
      getOutputStateName(
        freshSourceNode?.type as FlowNodeTypes,
        freshSourceNode?.data.config as NodeDataBase['config'],
        sourceHandleId,
      ),
    [freshSourceNode, sourceHandleId],
  )

  const opStateDescription = useMemo(
    () =>
      getOutputStateDescription(
        freshSourceNode?.type as FlowNodeTypes,
        freshSourceNode?.data.config as NodeDataBase['config'],
        sourceHandleId,
      ),
    [freshSourceNode, sourceHandleId],
  )

  const { unConnectedOpStates } = useOutputStates(source, freshSourceNode)

  // A pending edge dragged from a section pill carries the section in its
  // handle — scope the drop picker to that section's paths.
  const pendingSection = pendingEdgeSection(sourceHandleId)
  const pickerOpStates = useMemo(
    () =>
      pendingSection
        ? unConnectedOpStates.filter((state) => state.section === pendingSection)
        : unConnectedOpStates,
    [unConnectedOpStates, pendingSection],
  )

  const currentFlowType = useFlowStore((state) => state.currentFlowType)

  // Get edge issues from the store
  const hasIssues = !!(
    data?.meta?.issues &&
    Array.isArray(data.meta.issues) &&
    data.meta.issues.length
  )

  if (freshSourceNode?.type === FlowNodeTypes.start && currentFlowType !== 'omni') return null
  const isVoiceMailDeteced = opStateName === VOICEMAIL_DETECTED_INTENT.name
  const isChoosenPath = data?.isChosenPath || selected

  return (
    <div
      style={{
        position: 'absolute',
        transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
        pointerEvents: isVoiceMailDeteced ? 'none' : 'all',
        cursor: isVoiceMailDeteced ? 'default' : 'pointer',
      }}
      className={cn(
        'nodrag nopan',
        sourceHandleId,
        shouldFade && 'opacity-50',
        data?.isChosenPath && 'node-hover-gradient node-currently-executing chosen-path',
        selected && !data?.isChosenPath && 'node-hover-gradient selected chosen-path',
        hasIssues && 'node-hover-gradient node-has-issues chosen-path',
      )}
    >
      <Tooltip content={opStateDescription || 'Edit this connection'}>
        <EdgePopover
          id={id}
          sourceNode={freshSourceNode}
          opStates={pickerOpStates}
          pathsTitle={pendingSection ? `${pendingSection} — Select a path` : undefined}
          opStateName={opStateName || 'default-edge'}
          target={target}
          trigger={
            <div
              data-testid="button-edge-label-trigger"
              className={cn(
                'flex items-center justify-between bg-background px-2 py-1 rounded shadow-sm border-st2 border cursor-pointer relative',
                hasIssues && 'border-status-danger',
                isChoosenPath && 'z-10',
              )}
            >
              <div className="font-inter text-s-500 text-primary">
                {opStateName || `Select a path`}
              </div>
              {!isVoiceMailDeteced && (
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <Pencil className="size-3 text-secondary" />
                </div>
              )}
            </div>
          }
        />
      </Tooltip>
    </div>
  )
}
